import { Component, Prop, h } from '@stencil/core';
import { getConstant } from '../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../utils/helpers/hydration';

/**
 * Awards component for SignNow brand showcasing awards and badges
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @contentConfig Awards component for displaying company awards and badges
 * @example <sn-awards-type-1 variant="text-right" constants='{"AWARDS_HEADING":"Award-winning eSignature solution","AWARDS_BADGE_TITLE_ONE":"airSlate SignNow is a leader in Mid-Market E-Signature on G2","AWARDS_BADGE_TITLE_TWO":"airSlate SignNow is a leader in Enterprise E-Signature on G2","AWARDS_BADGE_TITLE_THREE":"airSlate SignNow is a leader in E-Signature on G2","AWARDS_BADGE_TITLE_FOUR":"airSlate SignNow is a leader in Small-Business E-Signature on G2","LINK__AWARDS_BADGE_ONE":"https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge","LINK__AWARDS_BADGE_TWO":"https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge","LINK__AWARDS_BADGE_THREE":"https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge","LINK__AWARDS_BADGE_FOUR":"https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge","LINK__IMAGE_AWARDS_BADGE_ONE":"https://marketing-static.signnow.com/350/images/global/badges/Mid-Marlet-Fall-2025.svg","LINK__IMAGE_AWARDS_BADGE_TWO":"https://marketing-static.signnow.com/350/images/global/badges/High-Performer-Enterprise-Winter-2025.svg","LINK__IMAGE_AWARDS_BADGE_THREE":"https://marketing-static.signnow.com/350/images/global/badges/Leader-Winter-2025.svg","LINK__IMAGE_AWARDS_BADGE_FOUR":"https://marketing-static.signnow.com/350/images/global/badges/Small-Business-Winter-2025.svg"}'></sn-awards-type-1>
 * @constant AWARDS_HEADING - "Award-winning eSignature solution" - Main heading for awards section
 * @constant AWARDS_BADGE_TITLE_ONE - "airSlate SignNow is a leader in Mid-Market E-Signature on G2" - Title for first badge
 * @constant AWARDS_BADGE_TITLE_TWO - "airSlate SignNow is a leader in Enterprise E-Signature on G2" - Title for second badge
 * @constant AWARDS_BADGE_TITLE_THREE - "airSlate SignNow is a leader in E-Signature on G2" - Title for third badge
 * @constant AWARDS_BADGE_TITLE_FOUR - "airSlate SignNow is a leader in Small-Business E-Signature on G2" - Title for fourth badge
 * @constant LINK__AWARDS_BADGE_ONE - "https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge" - Link for "airSlate SignNow is a leader in Mid-Market E-Signature on G2" badge
 * @constant LINK__AWARDS_BADGE_TWO - "https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge" - Link for "airSlate SignNow is a leader in Enterprise E-Signature on G2" badge
 * @constant LINK__AWARDS_BADGE_THREE - "https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge" - Link for "airSlate SignNow is a leader in E-Signature on G2" badge
 * @constant LINK__AWARDS_BADGE_FOUR - "https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge" - Link for "airSlate SignNow is a leader in Small-Business E-Signature on G2" badge
 * @constant LINK__IMAGE_AWARDS_BADGE_ONE - "https://marketing-static.signnow.com/350/images/global/badges/Mid-Marlet-Fall-2025.svg" - Image URL for "airSlate SignNow is a leader in Mid-Market E-Signature on G2" badge
 * @constant LINK__IMAGE_AWARDS_BADGE_TWO - "https://marketing-static.signnow.com/350/images/global/badges/High-Performer-Enterprise-Winter-2025.svg" - Image URL for "airSlate SignNow is a leader in Enterprise E-Signature on G2" badge
 * @constant LINK__IMAGE_AWARDS_BADGE_THREE - "https://marketing-static.signnow.com/350/images/global/badges/Leader-Winter-2025.svg" - Image URL for "airSlate SignNow is a leader in E-Signature on G2" badge
 * @constant LINK__IMAGE_AWARDS_BADGE_FOUR - "https://marketing-static.signnow.com/350/images/global/badges/Small-Business-Winter-2025.svg" - Image URL for "airSlate SignNow is a leader in Small-Business E-Signature on G2" badge
 */
@Component({
  tag: 'sn-awards-type-1',
  shadow: false,
})
export class SNAwardsType1 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * List of award badges to display
   * @dynamic
   * @type {array}
   * @.type {object}
   * @.property {string} src - Image source URL for the badge
   * @.property {string} alt - Image alt text for accessibility
   * @.property {string} link - Optional URL to make badge clickable
   * @.property {string} title - Optional title attribute for the link
   * @.required src
   * @.required alt
   * @minItems 1
   */

  @Prop() badges: any[] = [
    {
      src: 'https://marketing-static.signnow.com/350/images/global/badges/Mid-Marlet-Fall-2025.svg',
      alt: 'G2 Crowd Award',
      link: 'https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge',
      title: 'airSlate SignNow is a leader in Mid-Market E-Signature on G2',
    },
    {
      src: 'https://marketing-static.signnow.com/350/images/global/badges/High-Performer-Enterprise-Winter-2025.svg',
      alt: 'G2 Crowd Award',
      link: 'https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge',
      title: 'airSlate SignNow is a leader in Enterprise E-Signature on G2',
    },
    {
      src: 'https://marketing-static.signnow.com/350/images/global/badges/Leader-Winter-2025.svg',
      alt: 'G2 Crowd Award',
      link: 'https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge',
      title: 'airSlate SignNow is a leader in E-Signature on G2',
    },
    {
      src: 'https://marketing-static.signnow.com/350/images/global/badges/Small-Business-Winter-2025.svg',
      alt: 'G2 Crowd Award',
      link: 'https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge',
      title: 'airSlate SignNow is a leader in Small-Business E-Signature on G2',
    },
  ];

  /**
   * Text alignment variant: text-left or text-right
   */
  @Prop() variant?: 'text-left' | 'text-right';

  /**
   * Use smaller text size for long headings
   */
  @Prop() longText: boolean = false;

  /**
   * Theme variant
   */
  @Prop() theme: 'light' | 'dark' = 'light';

  /**
   * HTML tag for the heading element (h1, h2, h3, h4, h5, h6)
   * @default "h2"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h2';

  private readonly badgeConstantKeys = ['AWARDS_BADGE_TITLE_ONE', 'AWARDS_BADGE_TITLE_TWO', 'AWARDS_BADGE_TITLE_THREE', 'AWARDS_BADGE_TITLE_FOUR'];

  private readonly badgeLinkKeys = ['LINK__AWARDS_BADGE_ONE', 'LINK__AWARDS_BADGE_TWO', 'LINK__AWARDS_BADGE_THREE', 'LINK__AWARDS_BADGE_FOUR'];

  private readonly badgeImageKeys = ['LINK__IMAGE_AWARDS_BADGE_ONE', 'LINK__IMAGE_AWARDS_BADGE_TWO', 'LINK__IMAGE_AWARDS_BADGE_THREE', 'LINK__IMAGE_AWARDS_BADGE_FOUR'];

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  render() {
    const badgesWithConstants = this.badges.map((badge, index) => ({
      ...badge,
      title: getConstant(this.constants, this.badgeConstantKeys[index], badge.title),
      link: getConstant(this.constants, this.badgeLinkKeys[index], badge.link),
      src: getConstant(this.constants, this.badgeImageKeys[index], badge.src),
    }));

    return (
      <awards-type-1 badges={badgesWithConstants} variant={this.variant} longText={this.longText} theme={this.theme}>
        <me-heading slot="heading" variant="2" tag={this.headingTag}>
          {getConstant(this.constants, 'AWARDS_HEADING', 'Award-winning eSignature solution')}
        </me-heading>
      </awards-type-1>
    );
  }
}
